/**
 * useEntityComments hook
 *
 * Fetch + add + delete comments for a single org project or request (ST4).
 * Talks to /api/tenant/comments; surfaces the server's German error on failure.
 */

"use client";

import { useState, useEffect, useCallback } from "react";
import type { Comment, CommentEntityType } from "@/lib/comments/types";
export function useEntityComments(entityType: CommentEntityType, entityId: string) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const fetchComments = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/tenant/comments?entity_type=${entityType}&entity_id=${entityId}`);
      if (res.ok) {
        const json = await res.json();
        setComments(json.data ?? []);
      }
    } catch {
      // non-fatal — section shows empty
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId]);
  useEffect(() => {
    if (entityId) fetchComments();
  }, [entityId, fetchComments]);
  const addComment = useCallback(async (body: string): Promise<boolean> => {
    setSubmitting(true);
    try {
      const res_0 = await fetch("/api/tenant/comments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          body
        })
      });
      if (res_0.ok) {
        await fetchComments();
        return true;
      }
      const b = await res_0.json().catch(() => null);
      alert(b?.error ?? "Kommentar konnte nicht gespeichert werden");
      return false;
    } catch {
      alert("Kommentar konnte nicht gespeichert werden");
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [entityType, entityId, fetchComments]);
  const deleteComment = useCallback(async (id: string): Promise<void> => {
    try {
      const res_1 = await fetch(`/api/tenant/comments/${id}`, {
        method: "DELETE"
      });
      if (res_1.ok) {
        setComments(prev => prev.filter(c => c.id !== id));
      } else {
        const b_0 = await res_1.json().catch(() => null);
        alert(b_0?.error ?? "Kommentar konnte nicht gelöscht werden");
      }
    } catch {
      alert("Kommentar konnte nicht gelöscht werden");
    }
  }, []);
  return {
    comments,
    isLoading,
    submitting,
    addComment,
    deleteComment
  };
}